import {
    type ReactNode,
    type Ref,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    type ExpandedState,
    functionalUpdate,
    type OnChangeFn,
    type RowSelectionState,
    type SortingState,
} from "@tanstack/react-table";
import { TaskSortValue } from "@common/types/views/tasksView";
import type { View } from "@common/types/views/views";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import {
    DataGrid,
    type DataGridHandle,
    type DataGridRow,
    type DataGridVirtualizationOptions,
    toViewSorting,
    toViewSortValue,
    useDataGrid,
    useSavedViewState,
} from "@design-system/DataGrid";
import { useDynamicTopOffset } from "@design-system/hooks/useDynamicTopOffset";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { styleWithVars } from "@lib/cssVars";
import { DEFAULT_TASK_SORT } from "../taskListConstants";
import { TaskStatusTerminationConfirmationModal } from "../TaskStatusTerminationConfirmationModal";
import type { NestedTaskRow } from "../types";
import {
    getTaskListDraftRows,
    getTaskListEditing,
    useTaskListActions,
} from "./taskListActions";
import type { TaskListConfig } from "./taskListColumnConfigs";
import { buildTaskListColumns } from "./taskListColumns";
import {
    getTaskListStatus,
    getTaskRowId,
    getTaskRows,
    isGroupRow,
    toExpandedGroups,
} from "./taskListRows";
import styles from "./TaskListTable.module.scss";
import { TaskRowScope } from "./TaskRowScope";
import { useTaskListShortcuts } from "./useTaskListShortcuts";

interface TaskListTableProps {
    /** `null` while the tasks load. */
    groups: NestedTaskRow[] | null;
    /** `groups` are the previous query's while the next one loads (a view or filter change). */
    isRefetching?: boolean;
    config: TaskListConfig;
    view: View;
    onViewChange: (view: View) => void;
    sort: TaskSortValue;
    onSortChange: (sort: TaskSortValue) => void;
    expanded: ExpandedState;
    onExpandedChange: (expanded: ExpandedState) => void;
    /** The open task: drawn as the active row, and scrolled into view once it closes. */
    activeTaskId: string | null;
    onOpenTask: (row: NestedTaskRow) => void;
    rowSelection: RowSelectionState;
    onRowSelectionChange: (selection: RowSelectionState) => void;
    /** Adds the select column; draft rows are never selectable. */
    canSelectRows: boolean;
    isCrossWorkspace: boolean;
    /** The clients with services: only their tasks edit Service. */
    serviceWorkspaceIds: ReadonlySet<string>;
    /** The organization has close stages: Stage edits. */
    hasCloseStages: boolean;
    notice?: ReactNode;
    /** Replaces the default empty message, e.g. the task view's own empty state. */
    emptyState?: ReactNode;
    footer?: ReactNode;
    /** Set when the tasks failed to load: shows the error with Retry instead of the groups. */
    taskLoadError?: { onRetry: () => void };
    ref?: Ref<DataGridHandle>;
    virtualization?: DataGridVirtualizationOptions;
}

/**
 * The task list on DataGrid: groups are top-level rows with their tasks
 * nested, sorted on the server, with the saved view's columns. Clicking or
 * pressing Enter on a task opens it.
 */
export const TaskListTable = ({
    groups,
    isRefetching = false,
    config,
    view,
    onViewChange,
    sort,
    onSortChange,
    expanded,
    onExpandedChange,
    activeTaskId,
    onOpenTask,
    rowSelection,
    onRowSelectionChange,
    canSelectRows,
    isCrossWorkspace,
    serviceWorkspaceIds,
    hasCloseStages,
    notice,
    emptyState,
    footer,
    taskLoadError,
    ref,
    virtualization,
}: TaskListTableProps) => {
    const gridRef = useRef<DataGridHandle>(null);
    // React Compiler caches this callback by `ref`; a bare or useMemo-wrapped mergeRefs call stays uncached.
    const handleRef = (handle: DataGridHandle | null) =>
        mergeRefs([gridRef, ref])(handle);
    const containerRef = useRef<HTMLDivElement>(null);
    const { topOffset } = useDynamicTopOffset({ containerRef });
    const isTaskOpen = activeTaskId !== null;
    const isLoading = groups === null;
    // Autofocus scrolls to the first row: once a task has been open, closing it must not jump the list.
    const [hasOpenedTask, setHasOpenedTask] = useState(isTaskOpen);
    if (isTaskOpen && !hasOpenedTask) {
        setHasOpenedTask(true);
    }

    const { taskTitleInlineEdit: isTitleEditable } = useTypedFlags();
    const { actions, confirmation, hasCloseOptions } = useTaskListActions();
    const { columns, listEditing } = useMemo(
        () => ({
            columns: buildTaskListColumns({
                config,
                canSelectRows,
                isTitleEditable,
                actions,
            }),
            listEditing: getTaskListEditing({
                hasCloseOptions,
                serviceWorkspaceIds,
                hasCloseStages,
            }),
        }),
        [
            config,
            canSelectRows,
            isTitleEditable,
            actions,
            hasCloseOptions,
            serviceWorkspaceIds,
            hasCloseStages,
        ],
    );
    const draftRows = useMemo(() => getTaskListDraftRows(actions), [actions]);
    const savedViewState = useSavedViewState({ view, onViewChange, columns });

    const sorting = useMemo(
        () => toViewSorting({ sort, columns: config.columns }),
        [sort, config.columns],
    );
    const controlledRef = useLatestRef({
        sorting,
        sortColumns: config.columns,
        expanded,
        groups,
        rowSelection,
    });
    const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
        (updater) => {
            const latest = controlledRef.current;
            onSortChange(
                toViewSortValue({
                    sorting: functionalUpdate(updater, latest.sorting),
                    columns: latest.sortColumns,
                }) ?? DEFAULT_TASK_SORT,
            );
        },
        [onSortChange, controlledRef],
    );
    const handleExpandedChange = useCallback<OnChangeFn<ExpandedState>>(
        (updater) => {
            const latest = controlledRef.current;
            const nextExpanded = functionalUpdate(updater, latest.expanded);
            onExpandedChange(
                nextExpanded === true
                    ? toExpandedGroups(latest.groups ?? [])
                    : nextExpanded,
            );
        },
        [onExpandedChange, controlledRef],
    );
    const handleRowOpen = useCallback(
        (row: DataGridRow<NestedTaskRow>) => {
            if (!row.original.isGroup) {
                onOpenTask(row.original);
            }
        },
        [onOpenTask],
    );
    const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
        (updater) =>
            onRowSelectionChange(
                functionalUpdate(updater, controlledRef.current.rowSelection),
            ),
        [onRowSelectionChange, controlledRef],
    );
    const isRowSelectable = useCallback(
        (row: NestedTaskRow) => canSelectRows && !isBusyClosesDemoId(row.id),
        [canSelectRows],
    );

    const table = useDataGrid({
        data: groups ?? undefined,
        columns,
        getRowId: getTaskRowId,
        status: getTaskListStatus({
            isLoading,
            isRefetching,
            loadError: taskLoadError,
        }),
        noun: "tasks",
        gridLines: "groups",
        expanding: {
            state: expanded,
            onChange: handleExpandedChange,
            getSubRows: getTaskRows,
            parentRows: "summary",
            isParentRow: isGroupRow,
        },
        sorting: {
            mode: "server",
            state: sorting,
            onChange: handleSortingChange,
        },
        rowSelection: {
            state: rowSelection,
            onChange: handleRowSelectionChange,
            isRowSelectable,
            subRows: "cascade",
            isKeyboardSelectionDisabled: isTaskOpen,
        },
        listEditing,
        draftRows,
        onRowOpen: handleRowOpen,
        rowProvider: isCrossWorkspace ? TaskRowScope : undefined,
        virtualization,
        ...savedViewState,
    });

    const shortcuts = useTaskListShortcuts({
        gridRef,
        table: table.table,
        isEnabled: !isTaskOpen,
    });

    // The open task covers the list (TasksPageLayout), so scrolling it then
    // forces layout of the hidden list and mounts rows nobody sees. Scroll to
    // the last open task once the list shows again.
    const lastOpenTaskIdRef = useRef(activeTaskId);
    useEffect(() => {
        table.atoms.activeRowKey.set(activeTaskId);
        if (activeTaskId !== null) {
            lastOpenTaskIdRef.current = activeTaskId;
            return;
        }
        const lastOpenTaskId = lastOpenTaskIdRef.current;
        if (lastOpenTaskId !== null && !isLoading) {
            lastOpenTaskIdRef.current = null;
            gridRef.current?.scrollToRow(lastOpenTaskId);
        }
    }, [activeTaskId, isLoading, table.atoms.activeRowKey, gridRef]);

    return (
        <div
            ref={containerRef}
            className={styles.root}
            style={styleWithVars({
                "--task-list-top-offset": `${topOffset}px`,
            })}
            onPointerEnter={shortcuts.activate}
            onFocus={shortcuts.activate}
        >
            {notice}
            <DataGrid
                ref={handleRef}
                table={table}
                autoFocus={!hasOpenedTask}
                aria-label="Tasks"
                className={styles.table}
            >
                <DataGrid.Grid />
                <DataGrid.Empty>{emptyState}</DataGrid.Empty>
                <DataGrid.Error />
                <div className={styles.footer}>{footer}</div>
            </DataGrid>
            <TaskStatusTerminationConfirmationModal {...confirmation} />
        </div>
    );
};
